import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMediaLinks,
  addMediaLink,
  updateMediaLink,
  deleteMediaLink,
} from "../../redux/slice/mediaLinkSlice";
import { Edit, Delete as DeleteIcon } from "@mui/icons-material";

const MediaLinks = () => {
  const dispatch = useDispatch();
  const {
    list: mediaLinks,
    loading,
    error,
  } = useSelector((state) => state?.mediaLinks);

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ title: "", media: "" });
  const [currentId, setCurrentId] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    dispatch(fetchMediaLinks());
  }, [dispatch]);

  const handleOpen = (media = null) => {
    if (media) {
      setFormData({ title: media?.title, media: media?.media });
      setCurrentId(media?._id);
    } else {
      setFormData({ title: "", media: "" });
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ title: "", media: "" });
    setCurrentId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidUrl(formData.media)) {
      setSnackbarMessage("Invalid media URL");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      if (currentId) {
        await dispatch(
          updateMediaLink({ id: currentId, updatedMedia: formData })
        ).unwrap();
        setSnackbarMessage("Media link updated successfully");
      } else {
        await dispatch(addMediaLink(formData)).unwrap();
        setSnackbarMessage("Media link added successfully");
      }
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
      dispatch(fetchMediaLinks());
    } catch (err) {
      console.log(err);

      setSnackbarMessage(err.message || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteMediaLink(deleteId)).unwrap();
      setSnackbarMessage("Media link deleted successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchMediaLinks());
    } catch (err) {
      setSnackbarMessage(err.message || "Failed to delete media link");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedLinks = mediaLinks?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Media Links
      </Typography>

      <Button
        variant="contained"
        sx={{
          backgroundColor: "#e0752d",
          "&:hover": { backgroundColor: "#F68633" },
          textTransform: "none",
          mb: 2,
        }}
        onClick={() => handleOpen()}
      >
        Add Media Link
      </Button>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress sx={{ color: "#F68633" }} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : mediaLinks && mediaLinks.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLinks?.map((link) => (
                <TableRow key={link._id}>
                  <TableCell>{link.title}</TableCell>
                  <TableCell>
                    {link?.media?.includes("youtube.com") ? (
                      <iframe
                        width="200"
                        height="100"
                        src={link.media.replace("watch?v=", "embed/")}
                        frameBorder="0"
                        allowFullScreen
                        title={link.title}
                      />
                    ) : link?.media?.includes("facebook.com") ? (
                      <a
                        href={link.media}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Facebook Video
                      </a>
                    ) : (
                      <a
                        href={link.media}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.media}
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(link.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => handleOpen(link)}
                    >
                      <Edit />
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDelete(link._id)}
                    >
                      <DeleteIcon />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">No media links available.</Alert>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={mediaLinks?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {currentId ? "Edit Media Link" : "Add Media Link"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Media URL"
              name="media"
              value={formData.media}
              onChange={handleChange}
              required
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                backgroundColor: "#e0752d",
                "&:hover": { backgroundColor: "#F68633" },
              }}
            >
              {currentId ? "Update" : "Create"}
            </Button>
          </form>
        </Box>
      </Modal>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this media link? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MediaLinks;
